/**
 * Dashboard présences/absences des classes du formateur.
 *
 * KPIs sur les 30 derniers jours + top 10 absents + détail par classe.
 */
import { useState } from 'react';
import {
  BarChart3, Loader2, CheckCircle2, XCircle, Clock, AlertTriangle, ChevronDown,
} from 'lucide-react';
import { FormateurLayout } from '../FormateurLayout';
import { useDashboardPresencesFormateurQuery } from '../../services/formateurApi';

function dateISO(d: Date): string { return d.toISOString().slice(0, 10); }

export default function DashboardPresences() {
  const today = new Date();
  const ilYa30 = new Date(today); ilYa30.setDate(today.getDate() - 30);
  const [depuis, setDepuis] = useState(dateISO(ilYa30));
  const [jusqua, setJusqua] = useState(dateISO(today));
  const [classe, setClasse] = useState<string>('');

  const { data, isLoading } = useDashboardPresencesFormateurQuery({
    depuis, jusqua, classe: classe || undefined,
  });

  return (
    <FormateurLayout title="Présences & absences">
      {/* Filtres */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 mb-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[10px] uppercase font-semibold text-gray-400 mb-1">Du</label>
            <input type="date" value={depuis} onChange={(e) => setDepuis(e.target.value)}
              className="w-full px-2 py-1.5 text-xs bg-gray-800 border border-gray-700 rounded text-gray-100" />
          </div>
          <div>
            <label className="block text-[10px] uppercase font-semibold text-gray-400 mb-1">Au</label>
            <input type="date" value={jusqua} onChange={(e) => setJusqua(e.target.value)}
              className="w-full px-2 py-1.5 text-xs bg-gray-800 border border-gray-700 rounded text-gray-100" />
          </div>
        </div>
        <div className="relative">
          <select value={classe} onChange={(e) => setClasse(e.target.value)}
            className="w-full appearance-none px-3 py-1.5 pr-8 text-xs bg-gray-800 border border-gray-700 rounded text-gray-100">
            <option value="">Toutes mes classes</option>
            {/* Priorité : classes actuelles du formateur (peuplées même sans séance) */}
            {(data?.mes_classes && data.mes_classes.length > 0
              ? data.mes_classes.map(c => ({ classe: c }))
              : (data?.par_classe || [])
            ).map(c => (
              <option key={c.classe} value={c.classe}>{c.classe}</option>
            ))}
          </select>
          <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500" />
        </div>
      </div>

      {isLoading && (
        <div className="text-center py-8 text-gray-400 text-sm">
          <Loader2 size={16} className="inline animate-spin mr-2" /> Chargement…
        </div>
      )}

      {data && (
        <>
          {/* KPI taux */}
          <div className="bg-gradient-to-br from-emerald-900/40 to-emerald-900/10 border border-emerald-700/40 rounded-xl p-4 mb-3 text-center">
            <div className="text-3xl font-bold text-emerald-300">
              {data.kpi.taux_presence.toFixed(1)}%
            </div>
            <div className="text-[10px] uppercase font-semibold text-emerald-300/70 tracking-wider mt-1">
              Taux de présence
            </div>
            <div className="text-[10px] text-gray-400 mt-1">
              {data.kpi.nb_seances} séances • {data.kpi.total_pointages} pointages
            </div>
          </div>

          {/* KPI répartition */}
          <div className="grid grid-cols-4 gap-1.5 mb-4">
            <Kpi label="Présents" valeur={data.kpi.present} couleur="emerald" icon={<CheckCircle2 size={12} />} />
            <Kpi label="Absents" valeur={data.kpi.absent} couleur="red" icon={<XCircle size={12} />} />
            <Kpi label="Retards" valeur={data.kpi.retard} couleur="amber" icon={<Clock size={12} />} />
            <Kpi label="Justifiés" valeur={data.kpi.justifie} couleur="blue" icon={<AlertTriangle size={12} />} />
          </div>

          {/* Par classe */}
          {data.par_classe.length > 0 && (
            <div className="mb-4">
              <h2 className="text-[11px] uppercase tracking-wider font-bold text-gray-400 mb-2">
                Détail par classe
              </h2>
              <div className="space-y-1.5">
                {data.par_classe.map(c => (
                  <div key={c.classe} className="bg-gray-900 border border-gray-800 rounded-lg p-2.5 flex items-center gap-2.5">
                    <div className="flex-shrink-0 w-12 text-center font-bold text-amber-300 text-xs">
                      {c.classe}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs text-gray-400">{c.nb_seances} séances</span>
                        <span className={`text-xs font-bold ${
                          c.taux_presence >= 80 ? 'text-emerald-300'
                          : c.taux_presence >= 60 ? 'text-amber-300' : 'text-red-300'
                        }`}>{c.taux_presence.toFixed(0)}%</span>
                      </div>
                      <div className="h-1.5 bg-gray-800 rounded overflow-hidden">
                        <div className={`h-full ${
                          c.taux_presence >= 80 ? 'bg-emerald-500'
                          : c.taux_presence >= 60 ? 'bg-amber-500' : 'bg-red-500'
                        }`} style={{ width: `${c.taux_presence}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top absents */}
          {data.top_absents.length > 0 && (
            <div>
              <h2 className="text-[11px] uppercase tracking-wider font-bold text-gray-400 mb-2 flex items-center gap-1.5">
                <BarChart3 size={11} /> Top absents
              </h2>
              <div className="space-y-1">
                {data.top_absents.map((t, i) => (
                  <div key={t.matricule} className="bg-gray-900 border border-gray-800 rounded-lg p-2 flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-red-900/40 border border-red-700/60 text-red-300 text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-white truncate">
                        {t.prenoms} {t.nom}
                      </div>
                      <div className="text-[10px] text-gray-500 truncate">
                        {t.classe} • {t.matricule}
                      </div>
                    </div>
                    <div className="text-xs font-bold text-red-300">
                      {t.nb_absences} <span className="text-[9px] font-normal">abs.</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.kpi.nb_seances === 0 && (
            <div className="text-center py-8 text-gray-500 text-sm">
              Aucune séance avec appel fait sur cette période.
            </div>
          )}
        </>
      )}
    </FormateurLayout>
  );
}

function Kpi({ label, valeur, couleur, icon }: {
  label: string; valeur: number; couleur: 'emerald' | 'red' | 'amber' | 'blue';
  icon: React.ReactNode;
}) {
  const map = {
    emerald: 'bg-emerald-900/30 border-emerald-800/50 text-emerald-300',
    red: 'bg-red-900/30 border-red-800/50 text-red-300',
    amber: 'bg-amber-900/30 border-amber-800/50 text-amber-300',
    blue: 'bg-blue-900/30 border-blue-800/50 text-blue-300',
  };
  return (
    <div className={`px-2 py-1.5 rounded border text-center ${map[couleur]}`}>
      <div className="inline-flex items-center gap-0.5 text-[9px] uppercase font-semibold">
        {icon} {label}
      </div>
      <div className="text-lg font-bold text-white mt-0.5">{valeur}</div>
    </div>
  );
}
